import React, { useEffect, useState } from "react"
import { useParams } from "react-router-dom";
import DashboardHeader from "../../layouts/dashboard_header";
import SideMenu from "../../layouts/dashboard_menu";
import DashboardFooter from "../../layouts/dashboard_footer";
import { buildApiUrl, useBaseUrl } from "../../../utils/utils";

interface VitalReading {
    temp: number | null;
    heart: number | null;
    bp: string | null;
    bg: number | null;
    bmi: number | null;
    resp: number | null;
}

interface Medication {
    name: string;
    dose: string;
    duration: string;
}

interface FollowUp {
    observation: string;
    notes: string;
    conclusion: string;
}

interface HistoryEntry {
    vitals: VitalReading[];
    medication: Medication[];
    follow_up: FollowUp[];
    assesment: string;
    complaint: string;
    allergies: string;
    physical: string;
    examined_by: string;
    date: string;
}

const iconForIndex = (i: number) => {
    const icons = ['ri-stack-line', 'ri-vip-diamond-line', 'ri-gift-line', 'ri-shield-star-line', 'ri-user-smile-line', 'ri-fire-line'];
    return icons[i % icons.length];
};

const PatientHistory: React.FC = () => {
    const { patientCode } = useParams<{ patientCode: string }>();
    const baseUrl = useBaseUrl();
    const [entries, setEntries] = useState<HistoryEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        document.title = "Patient History Timeline - Uzima Healthcare";
        if (!patientCode) {
            setError('No patient specified.');
            setLoading(false);
            return;
        }
        (async () => {
            setLoading(true);
            setError('');
            try {
                const res = await fetch(buildApiUrl(baseUrl, `ehr/patient-records`), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: `patient_code=${encodeURIComponent(patientCode)}`,
                });
                const data = await res.json();
                if (res.ok && data.status === 'success') {
                    setEntries((data.data || []).slice().sort((a: HistoryEntry, b: HistoryEntry) =>
                        new Date(b.date).getTime() - new Date(a.date).getTime()));
                } else {
                    setError(data.message || 'Could not load this patient\'s history.');
                }
            } catch {
                setError('Network error while loading patient history.');
            } finally {
                setLoading(false);
            }
        })();
    }, [patientCode, baseUrl]);

    return (
        <>
            <DashboardHeader />
            <SideMenu />

            <div className={"main-content"}>

                <div className={"page-content"}>

                    <div className="container-fluid">

                        <div className="row">
                            <div className="col-12">
                                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                    <h4 className="mb-sm-0">History Timeline</h4>

                                    <div className="page-title-right">
                                        <ol className="breadcrumb m-0">
                                            <li className="breadcrumb-item"><a href="/institution/patients">Patients</a>
                                            </li>
                                            <li className="breadcrumb-item active">Patient History</li>
                                        </ol>
                                    </div>

                                </div>
                            </div>
                        </div>

                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : error ? (
                            <div className="alert alert-danger">{error}</div>
                        ) : entries.length === 0 ? (
                            <div className="text-center py-5">
                                <i className="ri-file-history-line" style={{ fontSize: '2.5rem' }}></i>
                                <h5 className="mt-3">No medical history recorded yet</h5>
                                <p className="text-muted mb-0">
                                    This patient has no medical records on file at this clinic. Records created
                                    during future visits will appear here as a timeline.
                                </p>
                            </div>
                        ) : (
                            <div className="row">
                                <div className="col-lg-12">
                                    <div>
                                        <h5>Visit Timeline</h5>
                                        <div className="timeline">
                                            {entries.map((entry, i) => (
                                                <div className={`timeline-item ${i % 2 === 0 ? 'left' : 'right'}`} key={i}>
                                                    <i className={`icon ${iconForIndex(i)}`}> </i>
                                                    <div className="date">{new Date(entry.date).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                                                    <div className="content">
                                                        <div className="d-flex">
                                                            <div className="flex-shrink-0">
                                                                <span className="avatar-sm rounded d-flex align-items-center justify-content-center bg-primary-subtle text-primary">
                                                                    <i className="ri-stethoscope-line fs-18"></i>
                                                                </span>
                                                            </div>
                                                            <div className="flex-grow-1 ms-3">
                                                                <h5 className="fs-15">{entry.complaint || 'Clinical Visit'}</h5>
                                                                <p className="text-muted mb-2">Examined by: {entry.examined_by || 'N/A'}</p>
                                                                {entry.assesment && <p className="text-muted mb-2">Assessment: {entry.assesment}</p>}
                                                                {entry.physical && <p className="text-muted mb-2">Physical: {entry.physical}</p>}
                                                                {entry.allergies && <p className="text-muted mb-2">Allergies: {entry.allergies}</p>}

                                                                {entry.vitals.length > 0 && (
                                                                    <div className="hstack gap-2 flex-wrap mb-2">
                                                                        {entry.vitals.map((v, vi) => (
                                                                            <React.Fragment key={vi}>
                                                                                {v.bp && <span className="badge bg-light text-dark">BP: {v.bp}</span>}
                                                                                {v.heart != null && <span className="badge bg-light text-dark">HR: {v.heart}</span>}
                                                                                {v.temp != null && <span className="badge bg-light text-dark">Temp: {v.temp}</span>}
                                                                                {v.bmi != null && <span className="badge bg-light text-dark">BMI: {v.bmi}</span>}
                                                                            </React.Fragment>
                                                                        ))}
                                                                    </div>
                                                                )}

                                                                {entry.medication.length > 0 && (
                                                                    <div className="mb-2">
                                                                        <small className="text-muted d-block">Medications:</small>
                                                                        {entry.medication.map((m, mi) => (
                                                                            <span className="badge bg-info-subtle text-info me-1" key={mi}>
                                                                                {m.name} {m.dose ? `(${m.dose})` : ''}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                )}

                                                                {entry.follow_up.length > 0 && (
                                                                    <div>
                                                                        <small className="text-muted d-block">Follow-up:</small>
                                                                        {entry.follow_up.map((f, fi) => (
                                                                            <p className="text-muted mb-1 small" key={fi}>{f.conclusion || f.observation}</p>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>

                </div>

                <DashboardFooter />
            </div>

        </>
    )
}

export default PatientHistory
